export type Role = 'owner' | 'admin' | 'audit_manager' | 'auditor' | 'reviewer' | 'client_viewer'

export interface Tenant { id: string; slug: string; display_name: string; base_currency: string; timezone: string }
export interface User { id: string; email: string; display_name: string }
export interface Me { user: User; tenant: Tenant; role: Role }

export interface Finding {
  id: string
  engagement_id: string
  client_id: string
  rule_code: string
  rule_version: number
  title: string
  summary: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  risk_score: number
  confidence: number
  amount_at_risk_satang: number
  currency: string
  status: string
  explanation: {
    steps?: string[]
    inputs?: Record<string, unknown>
    formula?: string
    risk?: Record<string, number>
    dedupe_key?: string
  }
  created_at: string
  updated_at: string
  evidence?: { id: string; kind: string; ref_id?: string; excerpt?: string }[]
}

export interface Kpis {
  findings_total: number
  findings_open: number
  critical: number
  high: number
  amount_at_risk_satang: number
  clients: number
  engagements: number
  transactions: number
}

export interface Engagement {
  id: string
  client_id: string
  code: string
  title: string
  period_start: string
  period_end: string
  status: string
  materiality_satang: number
  finding_count: number
  open_findings: number
  transaction_count: number
}

export interface Client { id: string; code: string; name: string; status: string }

export interface DocumentRow {
  id: string
  client_id: string
  engagement_id?: string
  kind: string
  original_name: string
  mime_type: string
  size_bytes: number
  status: string
  page_count: number
  created_at: string
}

export interface ExtractionField {
  field: string
  value_text?: string
  value_num?: number
  confidence: number
  page_no: number
}

export interface ExtractionView {
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'unsupported'
  engine?: string
  error?: string
  fields: ExtractionField[]
  result?: {
    kind: string
    valid: boolean
    confidence: number
    warnings: string[] | null
  }
}

export interface Plan {
  id: string
  name: string
  price_satang: number
  included_docs: number
  included_seats: number
  overage_satang: number
}

export interface Quote {
  base_satang: number
  included_docs: number
  used_docs: number
  overage_docs: number
  overage_satang: number
  subtotal_satang: number
  vat_satang: number
  total_satang: number
}

export interface BillingSnapshot {
  subscription: {
    id: string
    plan_id: string
    status: string
    seats: number
    current_period_start: string
    current_period_end: string
    trial_ends_at?: string
  }
  plan: Plan
  used_docs: number
  quote: Quote
  plans: Plan[]
}

export interface Invoice {
  id: string
  number: string
  period_start: string
  period_end: string
  plan_id: string
  base_satang: number
  overage_docs: number
  overage_satang: number
  subtotal_satang: number
  vat_satang: number
  total_satang: number
  currency: string
  status: string
  issued_at: string
  due_at: string
}

export interface RetentionPolicy {
  documents_days: number
  audit_days: number
  auto_purge: boolean
  updated_at?: string
}

export interface MfaStatus { enabled: boolean; label?: string }

export interface MfaEnrollment {
  secret: string
  otpauth_url: string
  digits: number
  period: number
}

export interface CaseRow {
  id: string
  case_no: string
  title: string
  status: string
  engagement_id: string
  client_id: string
  owner_id?: string
  opened_at: string
  closed_at?: string
  finding_count: number
  amount_at_risk_satang: number
}

export interface CaseEvent {
  id: number
  event_type: string
  from_status?: string
  to_status?: string
  actor_id?: string
  payload?: Record<string, unknown>
  created_at: string
}

export interface CaseDetail extends CaseRow {
  findings: Finding[]
  events: CaseEvent[]
}

export interface AuditEvent {
  id: number
  occurred_at: string
  actor_type: string
  actor_id?: string
  action: string
  resource_type: string
  resource_id?: string
  request_id?: string
  record_hash: string
}

export class ApiError extends Error {
  code: string
  status: number
  fields?: { field: string; message: string }[]
  constructor(status: number, code: string, message: string, fields?: { field: string; message: string }[]) {
    super(message)
    this.status = status
    this.code = code
    this.fields = fields
  }
}

let accessToken: string | null = null
let refreshing: Promise<boolean> | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

async function doRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch('/v1/auth/refresh', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(async (r) => {
        if (!r.ok) return false
        const body = await r.json()
        accessToken = body.data.access_token
        return true
      })
      .catch(() => false)
      .finally(() => { setTimeout(() => { refreshing = null }, 50) })
  }
  return refreshing
}

interface Envelope<T> { data: T; meta: { request_id: string; count?: number } }

export async function api<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  let res: Response
  try {
    res = await fetch(path, { ...init, headers, credentials: 'include' })
  } catch (err) {
    // A dropped connection (flaky mobile network, tunnel restart) should not blank the page.
    if (!retry) throw err
    await new Promise((r) => setTimeout(r, 250))
    return api<T>(path, init, false)
  }
  if (res.status === 401 && retry) {
    const ok = await doRefresh()
    if (ok) return api<T>(path, init, false)
  }
  // 502/503/504 come from the edge or the tunnel, not from the API's own logic: one retry is safe
  // for GETs and harmless for writes because Idempotency-Key makes a replay return the first result.
  if (retry && (res.status === 502 || res.status === 503 || res.status === 504)) {
    await new Promise((r) => setTimeout(r, 300))
    return api<T>(path, init, false)
  }
  const text = await res.text()
  const body = text ? JSON.parse(text) : {}
  if (!res.ok) {
    throw new ApiError(res.status, body.code ?? 'INTERNAL', body.detail || body.title || 'เกิดข้อผิดพลาด', body.errors)
  }
  return (body as Envelope<T>).data
}

export async function list<T>(path: string): Promise<T[]> {
  return api<T[]>(path)
}

export type LoginResult =
  | { mfa_required: true; mfa_token: string }
  | { mfa_required?: false; access_token: string; user: User; tenant: Tenant; role: Role }

export async function login(email: string, password: string): Promise<LoginResult> {
  const data = await api<LoginResult>('/v1/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) })
  if (!data.mfa_required) accessToken = data.access_token
  return data
}

export async function verifyMfa(mfaToken: string, code: string): Promise<{ user: User; tenant: Tenant; role: Role }> {
  const data = await api<{ access_token: string; user: User; tenant: Tenant; role: Role }>(
    '/v1/auth/mfa/verify', { method: 'POST', body: JSON.stringify({ mfa_token: mfaToken, code }) })
  accessToken = data.access_token
  return { user: data.user, tenant: data.tenant, role: data.role }
}

export async function bootstrap(): Promise<Me | null> {
  try {
    const me = await api<Me>('/v1/auth/me')
    return me
  } catch {
    return null
  }
}

export async function logout() {
  try { await api('/v1/auth/logout', { method: 'POST', body: '{}' }) } catch { /* ignore */ }
  accessToken = null
}

/** State-changing call with a fresh Idempotency-Key. */
export async function apiWrite<T>(path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown): Promise<T> {
  return api<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  })
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

export interface UploadTicket {
  document: { id: string; original_name: string; status: string }
  upload: { upload_id: string; url: string; method: string; headers: Record<string, string>; expires_at: string; complete_url: string }
}

/** Three-step upload: declare + hash, PUT the bytes, then confirm. */
export async function uploadDocument(file: File, clientId: string, engagementId: string, kind: string): Promise<UploadTicket['document']> {
  const hash = await sha256Hex(file)
  const ticket = await api<UploadTicket>('/v1/documents/uploads', {
    method: 'POST',
    headers: { 'Idempotency-Key': crypto.randomUUID() },
    body: JSON.stringify({
      client_id: clientId,
      engagement_id: engagementId,
      kind,
      filename: file.name,
      size_bytes: file.size,
      content_type: file.type || 'application/octet-stream',
      sha256: hash,
    }),
  })
  const put = await fetch(ticket.upload.url, {
    method: ticket.upload.method,
    headers: ticket.upload.headers,
    body: file,
  })
  if (!put.ok) throw new ApiError(put.status, 'UPLOAD_FAILED', 'อัปโหลดไฟล์ไม่สำเร็จ')
  return api<UploadTicket['document']>(ticket.upload.complete_url, { method: 'POST' })
}

/** Authenticated file download (CSV export). */
export async function reportLinks(engagementId?: string) {
  const qs = engagementId ? `?engagement_id=${encodeURIComponent(engagementId)}` : ''
  return api<{ pdf_url: string; xlsx_url: string; expires_at: string }>(`/v1/reports/downloads${qs}`)
}

export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(path, { credentials: 'include', headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} })
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'ดาวน์โหลดไม่สำเร็จ')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Global search across findings, documents, cases and engagements. */
export type SearchKind = 'finding' | 'document' | 'case' | 'engagement'

export interface SearchItem {
  id: string
  kind: SearchKind
  title: string
  subtitle: string
  href: string
}

export interface SearchGroup {
  kind: SearchKind
  label: string
  items: SearchItem[]
}

export interface SearchResults {
  groups: SearchGroup[]
  total: number
}

export async function searchAll(q: string, limit = 8): Promise<SearchResults> {
  const qs = new URLSearchParams({ q, limit: String(limit) })
  return api<SearchResults>(`/v1/search?${qs.toString()}`)
}


// ---- Whistleblower intake (part 177) -------------------------------------------------
export type WhistleCategory = 'fraud' | 'bribery' | 'harassment' | 'safety' | 'other'
export type WhistleStatus = 'received' | 'reviewing' | 'closed'

export interface WhistleReport {
  id: string
  category: WhistleCategory
  detail: string
  contact?: string
  evidence_note?: string
  status: WhistleStatus
  auditor_note?: string
  created_at: string
  updated_at: string
}

export interface WhistlePublic {
  category: WhistleCategory
  status: WhistleStatus
  created_at: string
  updated_at: string
  timeline: { event_type: string; status: WhistleStatus; at: string }[]
}

const tenantSlug = () => (import.meta.env.VITE_TENANT_SLUG as string | undefined) ?? 'demo-audit'

export async function submitWhistle(input: {
  category: WhistleCategory
  detail: string
  contact: string
  evidence_note: string
}): Promise<{ tracking_code: string; status: WhistleStatus; created_at: string; notice: string }> {
  return api(`/v1/whistle?tenant=${encodeURIComponent(tenantSlug())}`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function lookupWhistle(code: string): Promise<WhistlePublic> {
  return api(`/v1/whistle/${encodeURIComponent(code)}?tenant=${encodeURIComponent(tenantSlug())}`)
}

export async function listWhistle(): Promise<WhistleReport[]> {
  return api<WhistleReport[]>('/v1/whistle')
}

export async function transitionWhistle(id: string, status: WhistleStatus, auditorNote: string): Promise<WhistleReport> {
  return apiWrite<WhistleReport>(`/v1/whistle/${id}`, 'PATCH', { status, auditor_note: auditorNote })
}
